import { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography, Dialog } from "@mui/material";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { llmApi, projectsApi } from "@/services/rag-service";
import { organizationApi } from "@/services/organization-service";
import CrossIcon from "@/assets/icons/CrossIcon";
import { useAuth } from "@/contexts/AuthContext";
import ProjectResolutionList from "./components/ProjectResolutionList";
import ShareEmailInput from "./components/ShareEmailInput";
import SharedUsersList from "./components/SharedUsersList";
import type {
  AccessLevel,
  CatalogSelectOption,
  ExistingAccessLevel,
  LLMModel,
  Project,
  ProjectMembership,
  ShareUser,
} from "./types";
import { getUserId, getUserEmail, normalizeEmail } from "./utils/catalog";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type AICatalogShareProps = {
  open: boolean;
  onClose: () => void;
  model: LLMModel | null;
  onShared: () => void | Promise<void>;
};

type SharePayload = {
  invalidShareEmails: string[];
  organizationBlockedShareEmails: string[];
  blockedShareEmails: string[];
  readyShareEmails: string[];
};

type OrganizationMember = {
  email?: string;
  user_email?: string;
  user?: {
    email?: string;
    username?: string;
  };
};

const AICatalogShareComp = ({
  open,
  onClose,
  model,
  onShared,
}: AICatalogShareProps) => {
  const { t } = useTranslation();
  const { cache } = useAuth();
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [accessLevel, setAccessLevel] = useState<AccessLevel>("modifier");
  const [membershipByEmail, setMembershipByEmail] = useState<
    Record<string, ProjectMembership>
  >({});
  const [resolvedProjectEmails, setResolvedProjectEmails] = useState<
    Record<string, boolean>
  >({});
  const [linkedProject, setLinkedProject] = useState<Project | null>(null);
  const [organizationMembers, setOrganizationMembers] = useState<
    OrganizationMember[]
  >([]);
  const [sharedUsers, setSharedUsers] = useState<ShareUser[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [loadingProjectDetails, setLoadingProjectDetails] = useState(false);
  const [loadingOrganizationMembers, setLoadingOrganizationMembers] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addingToProject, setAddingToProject] = useState<
    Record<string, boolean>
  >({});
  const [updatingShare, setUpdatingShare] = useState<Record<string, boolean>>(
    {},
  );

  const accessLevels: CatalogSelectOption<AccessLevel>[] = useMemo(
    () => [
      { value: "modifier", label: t("aiCatalog.shareDialog.canEdit") },
      { value: "viewer", label: t("aiCatalog.shareDialog.canView") },
      { value: "admin", label: t("aiCatalog.shareDialog.canManage") },
    ],
    [t],
  );

  const projectId = model?.project_id;
  const organizationId =
    linkedProject?.organization_id || linkedProject?.organization?.id || null;
  const isOrganizationProject = Boolean(organizationId);
  const projectName =
    linkedProject?.name ||
    model?.project_name ||
    model?.project?.name ||
    t("aiCatalog.shareDialog.fallbackProjectName");
  const modelName = model?.name || t("aiCatalog.shareDialog.fallbackModelName");

  const organizationMemberEmails = useMemo(() => {
    const memberEmails = organizationMembers
      .map((member) =>
        normalizeEmail(
          member.user?.email ||
            member.user?.username ||
            member.email ||
            member.user_email ||
            "",
        ),
      )
      .filter(Boolean);

    return new Set(memberEmails);
  }, [organizationMembers]);

  const invalidEmails = useMemo(
    () => emails.filter((email) => !emailRegex.test(email)),
    [emails],
  );

  const organizationBlockedEmails = useMemo(
    () =>
      emails.filter(
        (email) =>
          isOrganizationProject &&
          emailRegex.test(email) &&
          !organizationMemberEmails.has(email),
      ),
    [emails, isOrganizationProject, organizationMemberEmails],
  );

  const blockedEmails = useMemo(
    () =>
      emails.filter(
        (email) =>
          emailRegex.test(email) &&
          !organizationBlockedEmails.includes(email) &&
          membershipByEmail[email]?.is_member === false,
      ),
    [emails, membershipByEmail, organizationBlockedEmails],
  );

  const projectResolutionEmails = useMemo(
    () =>
      emails.filter(
        (email) =>
          blockedEmails.includes(email) || resolvedProjectEmails[email],
      ),
    [emails, blockedEmails, resolvedProjectEmails],
  );

  const canShare = useMemo(() => {
    const typedEmails = emailInput
      .split(/[,\s]+/)
      .map(normalizeEmail)
      .filter(Boolean);
    const shareEmails = [...emails];

    typedEmails.forEach((email) => {
      if (!shareEmails.includes(email)) shareEmails.push(email);
    });

    return (
      !submitting &&
      !loadingProjectDetails &&
      !loadingOrganizationMembers &&
      shareEmails.length > 0 &&
      shareEmails.every((email) => emailRegex.test(email)) &&
      shareEmails.every(
        (email) =>
          !isOrganizationProject || organizationMemberEmails.has(email),
      ) &&
      shareEmails.every(
        (email) => membershipByEmail[email]?.is_member !== false,
      ) &&
      shareEmails.every(
        (email) =>
          !sharedUsers.find(
            (user) =>
              normalizeEmail(getUserEmail(user)) === email ||
              normalizeEmail(user.user_name || "") === email,
          ),
      )
    );
  }, [
    emailInput,
    emails,
    isOrganizationProject,
    loadingOrganizationMembers,
    loadingProjectDetails,
    membershipByEmail,
    organizationMemberEmails,
    sharedUsers,
    submitting,
  ]);

  const resetState = () => {
    setEmails([]);
    setEmailInput("");
    setAccessLevel("modifier");
    setMembershipByEmail({});
    setResolvedProjectEmails({});
    setLinkedProject(null);
    setOrganizationMembers([]);
    setSharedUsers([]);
    setLoadingShares(false);
    setLoadingProjectDetails(false);
    setLoadingOrganizationMembers(false);
    setSubmitting(false);
    setAddingToProject({});
    setUpdatingShare({});
  };

  const checkMembership = async (email: string) => {
    if (!projectId || !emailRegex.test(email)) return;

    try {
      const status = (await projectsApi.checkUserProjectMembership(
        projectId,
        email,
      )) as ProjectMembership;
      setMembershipByEmail((previous) => ({
        ...previous,
        [email]: status || { is_member: false },
      }));
    } catch {
      setMembershipByEmail((previous) => ({
        ...previous,
        [email]: { is_member: false },
      }));
    }
  };

  const addEmails = (rawValue: string) => {
    const nextEmails = rawValue
      .split(/[,\s]+/)
      .map(normalizeEmail)
      .filter(Boolean);

    const userFound = nextEmails.some((email) =>
      sharedUsers.find(
        (user) =>
          normalizeEmail(getUserEmail(user)) === email ||
          normalizeEmail(user.user_name || "") === email,
      ),
    );

    if (!nextEmails.length) return;
    if (!userFound) {
      setEmails((previous) => {
        const merged = [...previous];
        nextEmails.forEach((email) => {
          if (!merged.includes(email)) merged.push(email);
        });
        return merged;
      });

      nextEmails.forEach(checkMembership);
    }
    setEmailInput("");
  };

  const removeEmail = (email: string) => {
    setEmails((previous) => previous.filter((item) => item !== email));
    setResolvedProjectEmails((previous) => {
      const next = { ...previous };
      delete next[email];
      return next;
    });
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", "Tab", ","].includes(event.key)) {
      event.preventDefault();
      addEmails(emailInput);
    }

    if (event.key === "Backspace" && !emailInput && emails.length) {
      setEmails((previous) => previous.slice(0, -1));
    }
  };

  const loadSharedUsers = async () => {
    if (!model?.id) {
      setSharedUsers([]);
      return;
    }

    setLoadingShares(true);
    try {
      const users = (await llmApi.getLLMConfigShares(model.id)) as ShareUser[];
      setSharedUsers(Array.isArray(users) ? users : []);
    } catch (error) {
      console.error("Error loading model shares:", error);
      toast.error(t("aiCatalog.shareDialog.failedToLoadSharedUsers"));
    } finally {
      setLoadingShares(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    resetState();
    onClose();
  };

  const handleAddToProject = async (email: string) => {
    if (!projectId) return;

    setAddingToProject((previous) => ({ ...previous, [email]: true }));
    try {
      await projectsApi.shareProject(projectId, {
        email,
        access_level: accessLevel,
      });
      setMembershipByEmail((previous) => ({
        ...previous,
        [email]: { is_member: true, access_level: accessLevel },
      }));
      setResolvedProjectEmails((previous) => ({ ...previous, [email]: true }));
      toast.success(
        t("aiCatalog.shareDialog.addedToProjectSuccess", { email }),
      );
    } catch (error) {
      console.error("Error adding user to project:", error);
      toast.error(
        (error as Error).message ||
          t("aiCatalog.shareDialog.failedToAddToProject"),
      );
    } finally {
      setAddingToProject((previous) => ({ ...previous, [email]: false }));
    }
  };

  const getSharePayload = async (): Promise<SharePayload> => {
    const typedEmails = emailInput
      .split(/[,\s]+/)
      .map(normalizeEmail)
      .filter(Boolean);
    const shareEmails = [...emails];

    typedEmails.forEach((email) => {
      if (!shareEmails.includes(email)) shareEmails.push(email);
    });

    const invalidShareEmails = shareEmails.filter(
      (email) => !emailRegex.test(email),
    );
    const organizationBlockedShareEmails = shareEmails.filter(
      (email) =>
        isOrganizationProject &&
        emailRegex.test(email) &&
        !organizationMemberEmails.has(email),
    );
    let nextMembershipByEmail = membershipByEmail;

    if (projectId) {
      const emailsToCheck = shareEmails.filter(
        (email) =>
          emailRegex.test(email) &&
          !organizationBlockedShareEmails.includes(email) &&
          !nextMembershipByEmail[email],
      );

      if (emailsToCheck.length) {
        const checks = await Promise.all(
          emailsToCheck.map(async (email) => {
            try {
              const status = (await projectsApi.checkUserProjectMembership(
                projectId,
                email,
              )) as ProjectMembership;
              return [email, status || { is_member: false }] as const;
            } catch {
              return [email, { is_member: false }] as const;
            }
          }),
        );

        nextMembershipByEmail = {
          ...nextMembershipByEmail,
          ...Object.fromEntries(checks),
        };
        setMembershipByEmail(nextMembershipByEmail);
      }
    }

    if (typedEmails.length) {
      setEmails(shareEmails);
      setEmailInput("");
    }

    const blockedShareEmails = shareEmails.filter(
      (email) =>
        emailRegex.test(email) &&
        !organizationBlockedShareEmails.includes(email) &&
        nextMembershipByEmail[email]?.is_member === false,
    );
    const readyShareEmails = shareEmails.filter(
      (email) =>
        emailRegex.test(email) &&
        !organizationBlockedShareEmails.includes(email) &&
        nextMembershipByEmail[email]?.is_member !== false,
    );

    return {
      invalidShareEmails,
      organizationBlockedShareEmails,
      blockedShareEmails,
      readyShareEmails,
    };
  };

  const handleShare = async () => {
    if (!model?.id) return;

    const {
      invalidShareEmails,
      organizationBlockedShareEmails,
      blockedShareEmails,
      readyShareEmails,
    } = await getSharePayload();

    if (
      !readyShareEmails.length ||
      invalidShareEmails.length ||
      organizationBlockedShareEmails.length ||
      blockedShareEmails.length
    ) {
      return;
    }

    setSubmitting(true);
    try {
      await Promise.all(
        readyShareEmails.map((email) =>
          llmApi.shareLLMConfig(model.id, {
            user: email,
            role: accessLevel,
          } as any),
        ),
      );

      toast.success(t("aiCatalog.shareDialog.modelSharedSuccess"));
      setEmails([]);
      setEmailInput("");
      setResolvedProjectEmails((previous) => {
        const next = { ...previous };
        readyShareEmails.forEach((email) => {
          delete next[email];
        });
        return next;
      });
      await loadSharedUsers();
      await onShared();
    } catch (error) {
      console.error("Error sharing model:", error);
      toast.error(
        (error as Error).message ||
          t("aiCatalog.shareDialog.failedToShareModel"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveUserShare = async (email: string, user_id: string) => {
    if (!model || !email) return;
    const removedEmail = normalizeEmail(email);
    setUpdatingShare((previous) => ({ ...previous, [email]: true }));
    try {
      await llmApi.unshareByUserId(model.id, user_id);
      setSharedUsers((previous) =>
        previous.filter(
          (user) => normalizeEmail(getUserEmail(user)) !== removedEmail,
        ),
      );
      toast.success(t("aiCatalog.userAccessRemovedSuccess"));
      await loadSharedUsers();
      setSharedUsers((previous) =>
        previous.filter(
          (user) => normalizeEmail(getUserEmail(user)) !== removedEmail,
        ),
      );
    } catch (error) {
      console.error("Error removing user access:", error);
      toast.error(t("aiCatalog.failedToRemoveUserAccess"));
    } finally {
      setUpdatingShare((previous) => ({ ...previous, [email]: false }));
    }
  };

  const handleExistingAccessChange = async (
    user: ShareUser,
    nextAccessLevel: ExistingAccessLevel,
  ) => {
    const email = getUserEmail(user);
    const user_id = getUserId(user);
    if (!email || !user_id || !model?.id) return;

    if (nextAccessLevel === "remove") {
      handleRemoveUserShare(email, user_id);
      return;
    }

    setUpdatingShare((previous) => ({ ...previous, [email]: true }));
    try {
      await llmApi.shareLLMConfig(model.id, {
        user: email,
        role: nextAccessLevel,
      } as any);
      setSharedUsers((previous) =>
        previous.map((item) =>
          getUserEmail(item) === email
            ? { ...item, access_level: nextAccessLevel }
            : item,
        ),
      );
      toast.success(t("aiCatalog.shareDialog.accessUpdated"));
      await onShared();
    } catch (error) {
      console.error("Error updating model share:", error);
      toast.error(
        (error as Error).message ||
          t("aiCatalog.shareDialog.failedToUpdateAccess"),
      );
    } finally {
      setUpdatingShare((previous) => ({ ...previous, [email]: false }));
    }
  };

  useEffect(() => {
    if (open) loadSharedUsers();
  }, [open, model?.id]);

  useEffect(() => {
    if (!open || !projectId) {
      setLinkedProject(null);
      setOrganizationMembers([]);
      return;
    }

    let isCurrent = true;

    const loadLinkedProject = async () => {
      setLoadingProjectDetails(true);
      try {
        const project = (await projectsApi.getProject(
          projectId,
          false,
        )) as Project;
        if (isCurrent) setLinkedProject(project);
      } catch (error) {
        console.error("Error loading linked project details:", error);
        if (isCurrent) setLinkedProject(null);
      } finally {
        if (isCurrent) setLoadingProjectDetails(false);
      }
    };

    loadLinkedProject();

    return () => {
      isCurrent = false;
    };
  }, [open, projectId]);

  useEffect(() => {
    if (!open || !organizationId) {
      setOrganizationMembers([]);
      setLoadingOrganizationMembers(false);
      return;
    }

    let isCurrent = true;

    const loadOrganizationMembers = async () => {
      setLoadingOrganizationMembers(true);
      try {
        const response = await organizationApi.getOrganizationMembers(
          organizationId,
        );
        if (isCurrent) {
          setOrganizationMembers(
            Array.isArray(response.data) ? response.data : [],
          );
        }
      } catch (error) {
        console.error("Error loading organization members:", error);
        if (isCurrent) setOrganizationMembers([]);
      } finally {
        if (isCurrent) setLoadingOrganizationMembers(false);
      }
    };

    loadOrganizationMembers();

    return () => {
      isCurrent = false;
    };
  }, [open, organizationId]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        className:
          "w-[650px] max-w-[calc(100vw_-_32px)] rounded-lg border border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] shadow-[0_24px_80px_rgba(0,0,0,0.36)]",
      }}
    >
      <Box className="flex items-center justify-between px-5 py-4">
        <Typography className="text-[20px] font-semibold text-[var(--card-foreground)]">
          {t("aiCatalog.shareDialog.title", { name: modelName })}
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={submitting}
          data-testid="ai-catalog-share-close-button"
        >
          <CrossIcon className="h-5 w-5 text-[var(--muted-foreground)]" />
        </IconButton>
      </Box>

      <Box className="px-5 py-4">
        <Typography className="text-xs font-medium text-[var(--muted-foreground)] mb-2">
          {t("aiCatalog.shareDialog.emails")}
        </Typography>

        <ShareEmailInput
          t={t}
          emails={emails}
          emailInput={emailInput}
          accessLevel={accessLevel}
          accessLevels={accessLevels}
          blockedEmails={blockedEmails}
          submitting={submitting}
          canShare={canShare}
          canSubmitModel={Boolean(model?.id)}
          onEmailInputChange={setEmailInput}
          onInputBlur={() => addEmails(emailInput)}
          onInputKeyDown={handleInputKeyDown}
          onRemoveEmail={removeEmail}
          onAccessLevelChange={setAccessLevel}
          onShare={handleShare}
          resolvedProjectEmails={resolvedProjectEmails}
        />

        {(projectResolutionEmails.length > 0 || invalidEmails.length > 0) && (
          <Typography className="text-xs text-[var(--muted-foreground)] mt-2">
            {projectResolutionEmails.length > 0
              ? t("aiCatalog.shareDialog.notProjectMembers", {
                  project: projectName,
                })
              : t("aiCatalog.shareDialog.fixInvalidEmails")}
          </Typography>
        )}

        {organizationBlockedEmails.length > 0 ? (
          <Box className="mt-3 rounded-[8px] border border-[var(--destructive)] bg-[var(--destructive)]/10 p-3">
            <Typography className="text-xs font-medium text-[var(--destructive)]">
              {t("aiCatalog.shareDialog.notOrganizationMembers")}
            </Typography>
            <Box className="mt-2 flex flex-wrap gap-1.5">
              {organizationBlockedEmails.map((email) => (
                <Typography
                  key={email}
                  className="rounded bg-[var(--background)] px-2 py-1 text-xs text-[var(--foreground)]"
                >
                  {email}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null}

        <ProjectResolutionList
          t={t}
          emails={projectResolutionEmails}
          addingToProject={addingToProject}
          resolvedProjectEmails={resolvedProjectEmails}
          disabled={loadingProjectDetails || loadingOrganizationMembers}
          onAddToProject={handleAddToProject}
          onRemoveEmail={removeEmail}
        />

        <Typography className="text-sm text-[var(--muted-foreground)] mt-6 mb-3">
          {t("aiCatalog.shareDialog.sharedWith")}
        </Typography>

        <SharedUsersList
          t={t}
          users={sharedUsers}
          loading={loadingShares}
          currentUserId={cache?.id}
          accessLevels={accessLevels}
          updatingShare={updatingShare}
          onAccessChange={handleExistingAccessChange}
        />
      </Box>
    </Dialog>
  );
};

export default AICatalogShareComp;
